import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LockKeyhole, PlayCircle } from 'lucide-react'
import { formatMoney, openShiftSchema, toMinor } from '@najah/core'
import { Alert, Button, Card, CardBody, Input, Select, useToast } from '@najah/ui'
import { shiftsApi, type RegisterOption } from '../../lib/salesApi'
import { useFormErrors } from '../../lib/useFormErrors'

/** شاشة فتح الوردية — لا بيع قبل فتح صندوق، وإلا لم نعرف أين تُحسب المقبوضات */
export function ShiftGate({ registers }: { registers: RegisterOption[] }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, validate, handleApiError } = useFormErrors()

  const [registerId, setRegisterId] = useState(registers[0]?.id ?? '')
  const [openingCash, setOpeningCash] = useState('')

  const openMutation = useMutation({
    mutationFn: async () => {
      const parsed = validate(openShiftSchema, {
        registerId,
        openingCash: toMinor(Number(openingCash) || 0),
      })
      if (!parsed) throw new Error('VALIDATION')
      return shiftsApi.open(parsed)
    },
    onSuccess: (shift) => {
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      toast.success('فُتحت الوردية', `${shift.registerName} — ${formatMoney(shift.openingCash)}`)
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر فتح الوردية')
    },
  })

  return (
    <div className="grid min-h-[70dvh] place-items-center px-4">
      <Card className="w-full max-w-md">
        <CardBody className="space-y-5 p-6">
          <div className="text-center">
            <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-primary-subtle text-primary-subtle-fg">
              <LockKeyhole className="size-7" />
            </span>
            <h1 className="mt-4 font-display text-xl font-bold text-fg">افتح وردية للبدء</h1>
            <p className="mt-1.5 text-sm text-fg-muted">
              عُدّ النقد الموجود في الدرج الآن وأدخِله — عليه يُبنى جرد الصندوق عند الإغلاق.
            </p>
          </div>

          {formError && <Alert tone="danger">{formError}</Alert>}

          {registers.length === 0 ? (
            <Alert tone="warning" title="لا توجد صناديق">
              أضف صندوقاً من الإعدادات قبل بدء البيع.
            </Alert>
          ) : (
            <>
              <Select
                label="الصندوق" required
                value={registerId}
                onChange={(e) => setRegisterId(e.target.value)}
                options={registers.map((r) => ({ value: r.id, label: `${r.nameAr} (${r.code})` }))}
                error={errors.registerId}
              />
              <Input
                label="النقد في الدرج" required type="number" min="0" step="0.01" ltr
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                error={errors.openingCash}
                hint="دج"
                autoFocus
              />
              <Button
                fullWidth size="lg"
                onClick={() => openMutation.mutate()}
                loading={openMutation.isPending}
                startIcon={<PlayCircle className="size-5" />}
              >
                فتح الوردية
              </Button>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
